import * as React from 'react'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useGraphQLClient } from 'modules/core/lib/graphql/provider'

import { LabourUnitsResult } from 'modules/timeoff/hooks/useLabourUnits'
import type LeaveDayTypeForm from 'modules/timeoff/lib/LeaveDayTypeForm'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { DaysTree } from 'modules/timeoff/lib/getDateRangeFromTree'
import LeavesToDateTree from 'modules/timeoff/lib/toDateTree/Leaves'
import leaves from 'modules/timeoff/store/collections/leaves'

import Days from './DayTypes/Days'
import HalfDay from './DayTypes/HalfDay'
import Hours from './DayTypes/Hours'

interface Props {
  readOnly: boolean
  form: LeaveForm
  dayTypeForm: LeaveDayTypeForm
  companyHolidaysMuncher: () => DaysTree
  blockedPeriodsMuncher: () => DaysTree
  timeTaken?: string
  labourUnits: LabourUnitsResult
  isReviewOrEditMode?: boolean
  leaveId: number | undefined
  leaveTypeId: number
}

const DayTypeForm = ({
  readOnly = false,
  form,
  dayTypeForm,
  companyHolidaysMuncher,
  blockedPeriodsMuncher,
  timeTaken,
  labourUnits,
  isReviewOrEditMode,
  leaveId,
  leaveTypeId,
}: Props) => {
  const graphqlClient = useGraphQLClient()

  if (!graphqlClient) {
    throw new Error('GraphQL client not found')
  }
  const dayType = dayTypeForm.get('day_type').value

  // NOTE: this is not handling the loading state properly
  const { value: existingLeaves } = useAsyncData(() => {
    const existingLeavesToBlockOverlap = leaves.filter((leaveToFilter) =>
      leaveId
        ? leaveToFilter.attributes.id !== Number(leaveId)
        : leaveToFilter.attributes.leave_type_id === Number(leaveTypeId)
    )

    return new LeavesToDateTree(existingLeavesToBlockOverlap).munch(graphqlClient)
  }, [leaveTypeId])

  const props = {
    form,
    readOnly,
    holidays: companyHolidaysMuncher(),
    blockedPeriods: blockedPeriodsMuncher(),
    existingLeaves: existingLeaves || {},
    timeTaken,
    labourUnits,
    isReviewOrEditMode,
  }

  switch (dayType) {
    case 'half_day':
      return <HalfDay {...props} />
    case 'hours':
      return <Hours {...props} />

    default:
      return <Days {...props} />
  }
}

export default observer(DayTypeForm)
